import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { agentApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'

export function useApiKeys() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  const list = useQuery({
    queryKey: keys.apiKeys(),
    queryFn: () => agentApi.listKeys(),
  })

  const create = useMutation({
    mutationFn: (name: string) => agentApi.createKey(name),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.apiKeys() }),
    onError: () => toast.error(t('keys.mintFailed')),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => agentApi.revokeKey(id),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.apiKeys() })
      toast.show(t('keys.revoked'))
    },
    onError: () => toast.error(t('keys.revokeFailed')),
  })

  return { list, create, revoke }
}

/** agent 签名公钥：登记、轮换、吊销。 */
export function useSigningKeys() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  const list = useQuery({
    queryKey: ['agent', 'signing-keys'],
    queryFn: () => agentApi.listSigningKeys(),
  })

  const invalidate = () => {
    client.invalidateQueries({ queryKey: ['agent', 'signing-keys'] })
    client.invalidateQueries({ queryKey: keys.me() })
  }

  const register = useMutation({
    mutationFn: ({ publicKey, label }: { publicKey: string; label?: string }) =>
      agentApi.registerSigningKey(publicKey, label),
    onSuccess: () => {
      invalidate()
      toast.show(t('keys.signingAdded'))
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : t('keys.signingFailed')),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => agentApi.revokeSigningKey(id),
    onSuccess: () => {
      invalidate()
      toast.show(t('keys.signingRevoked'))
    },
    onError: () => toast.error(t('keys.revokeFailed')),
  })

  return { list, register, revoke }
}
